import { FC, useState } from 'react'
import { observer } from 'mobx-react'
import Modal from 'react-bootstrap/lib/Modal'
import { t } from 'ttag'

import { Member } from '../../../models3/ProjectModels'
import { OKButton, CancelButton } from '../../utils/Buttons'
import { MemberDisplay } from '../../utils/MemberDisplay'
import './MembersSelector.css'

interface IMemberSelectorItem {
    member: Member,
    selected: boolean,
    onToggle: () => void,
}

const MemberSelectorItem: FC<IMemberSelectorItem> = ({ member, selected, onToggle }) => {
    return (
        <div className="member-selector-item">
            <input
                type="checkbox"
                id={`member-${member.email}`}
                checked={selected}
                onChange={onToggle}
                className="member-selector-checkbox"
            />
            <label htmlFor={`member-${member.email}`} className="member-selector-label">
                <MemberDisplay member={member} imageSize="small" />
            </label>
        </div>
    )
}

interface IMembersSelector {
    members: Member[],
    header: string,
    show: boolean,
    onHide: () => void,
    setSelected: (selectedMembers: Member[]) => void,
    okButtonTooltip: string,
}

export const MembersSelector: FC<IMembersSelector> = observer(({ members, header, show, onHide, setSelected, okButtonTooltip }) => {
    const [selectedMemberEmails, setSelectedMemberEmails] = useState<Set<string>>(new Set())

    const handleMemberToggle = (member: Member) => {
        const newSelected = new Set(selectedMemberEmails)
        if (newSelected.has(member.email)) {
            newSelected.delete(member.email)
        } else {
            newSelected.add(member.email)
        }
        setSelectedMemberEmails(newSelected)
    }

    const handleOK = () => {
        const selectedMembers = members.filter(member => selectedMemberEmails.has(member.email))
        setSelected(selectedMembers)
        onHide()
    }

    const handleCancel = () => {
        setSelectedMemberEmails(new Set()) // Reset selection
        onHide()
    }

    // Split members into two columns
    const midpoint = Math.ceil(members.length / 2)
    const leftColumnMembers = members.slice(0, midpoint)
    const rightColumnMembers = members.slice(midpoint)

    return (
        <Modal show={show} onHide={handleCancel} bsSize="large">
            <Modal.Header closeButton>
                <Modal.Title>{header}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="members-selector-container">
                    <div className="members-selector-columns">
                        <div className="members-selector-column">
                            {leftColumnMembers.map(member => (
                                <MemberSelectorItem
                                    key={member.email}
                                    member={member}
                                    selected={selectedMemberEmails.has(member.email)}
                                    onToggle={() => handleMemberToggle(member)}
                                />
                            ))}
                        </div>
                        <div className="members-selector-column">
                            {rightColumnMembers.map(member => (
                                <MemberSelectorItem
                                    key={member.email}
                                    member={member}
                                    selected={selectedMemberEmails.has(member.email)}
                                    onToggle={() => handleMemberToggle(member)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', width: '100%' }}>
                    <OKButton
                        enabled={true}
                        onClick={handleOK}
                        buttonClassName=""
                        className="ok-button"
                        tooltip={okButtonTooltip}
                    />
                    <CancelButton
                        enabled={true}
                        onClick={handleCancel}
                        className="cancel-button"
                        tooltip={t`Cancel`}
                    />
                </div>
            </Modal.Footer>
        </Modal>
    )
})

export default MembersSelector